import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../lib/api";
import {
  getPeriodOptions,
  readPeriodParam,
  setPeriodParam,
  withPeriod,
} from "../lib/periods";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Loader2, ArrowLeft, TrendingUp, TrendingDown, DollarSign, Calendar,
  Wallet, Flame, Clock, BarChart3, PieChart as PieChartIcon, 
  LineChart as LineChartIcon, TableIcon, Download, Info
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend, ComposedChart
} from "recharts";

const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

// Projection horizon, derived the same way as on AdminDashboardPage: take the
// backend's runway_months, round it up and add one month of headroom so the
// line runs past the end of the runway. Capped at 36 months and never below
// 1, so a missing or odd runway value can never produce an endless loop.
const PROJECTION_MAX_MONTHS = 36;

const projectionHorizonMonths = (runwayMonths) =>
  Math.max(1, Math.min(PROJECTION_MAX_MONTHS, Math.ceil(runwayMonths || 0) + 1));

const MetricDetailPage = () => {
  const { metricType } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  // The period lives in the query string so it survives navigation.
  const selectedQuarter = readPeriodParam(searchParams);
  const handlePeriodChange = (value) =>
    setSearchParams(setPeriodParam(searchParams, value), { replace: true });
  const [viewMode, setViewMode] = useState("chart");

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const quarter = selectedQuarter === "all" ? undefined : selectedQuarter;
      const res = await apiService.getDashboardAnalytics(quarter);
      setAnalytics(res.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedQuarter]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // One shared option list for every analytics page (see lib/periods.js).
  const periodOptions = getPeriodOptions();

  const getMetricConfig = () => {
    switch (metricType) {
      case "cash-balance":
        return {
          title: "Cash Balance",
          icon: Wallet,
          color: "emerald",
          description: "Total available cash across all connected bank accounts",
          value: analytics?.cash_balance,
          insights: [
            { label: "Real Revenue", value: analytics?.real_revenue, color: "emerald" },
            // Split, because the total mixed share capital with borrowed money
            // and this page is where somebody goes to understand the figure.
            { label: "Share capital", value: analytics?.equity_raised, color: "blue" },
            { label: "Borrowed (has to go back)", value: analytics?.loans_received, color: "violet" },
            { label: "Operating Expenses", value: analytics?.total_expense, color: "red" },
            { label: "Operating Cash Flow", value: analytics?.operating_cash_flow, color: analytics?.operating_cash_flow >= 0 ? "emerald" : "red" },
          ]
        };
      case "burn-rate":
        return {
          title: "Burn Rate",
          icon: Flame,
          color: "red",
          // It excludes more than income: `burn_rate` is built from operating
          // spend only, so satellite funding and financing costs are out too.
          // The Fixed and Variable figures below now sum to that same basis.
          description: "Average monthly operating spend, excluding group funding and financing costs",
          value: analytics?.burn_rate,
          suffix: "/month",
          insights: [
            { label: "Fixed Expenses", value: analytics?.fixed_expenses, color: "blue" },
            { label: "Variable Expenses", value: analytics?.variable_expenses, color: "violet" },
            { label: "Month-over-Month Change", value: `${analytics?.burn_rate_change > 0 ? '+' : ''}${analytics?.burn_rate_change || 0}%`, isPercentage: true, color: analytics?.burn_rate_change > 0 ? "red" : "emerald" },
          ]
        };
      case "runway":
        return {
          title: "Runway",
          icon: Clock,
          color: "blue",
          description: "Estimated months of operation at current burn rate",
          value: analytics?.runway_months,
          suffix: " months",
          isInfinity: analytics?.runway_months === null,
          insights: [
            { label: "Cash Balance", value: analytics?.cash_balance, color: "emerald" },
            { label: "Monthly Burn", value: analytics?.burn_rate, color: "red" },
            { label: "Runway Health", value: analytics?.runway_months === null ? "Sustainable" : analytics?.runway_months < 6 ? "Critical" : analytics?.runway_months < 12 ? "Caution" : "Healthy", isStatus: true, color: analytics?.runway_months === null ? "emerald" : analytics?.runway_months < 6 ? "red" : analytics?.runway_months < 12 ? "amber" : "emerald" },
          ]
        };
      case "net-cash-flow":
        return {
          title: "Net Cash Flow",
          icon: analytics?.operating_cash_flow >= 0 ? TrendingUp : TrendingDown,
          color: analytics?.operating_cash_flow >= 0 ? "emerald" : "red",
          description: "Operating cash flow: real revenue minus operating expenses, excluding funding received and refunds",
          value: analytics?.operating_cash_flow,
          insights: [
            { label: "Real Revenue", value: analytics?.real_revenue, color: "emerald" },
            { label: "Operating Expenses", value: analytics?.total_expense, color: "red" },
            { label: "Share capital (excluded)", value: analytics?.equity_raised, color: "blue" },
            { label: "Borrowed (excluded)", value: analytics?.loans_received, color: "violet" },
            { label: "Net Cash Movement (incl. financing)", value: analytics?.net_cash_flow, color: analytics?.net_cash_flow >= 0 ? "emerald" : "red" },
          ]
        };
      case "spend-by-category":
        return {
          title: "Spend by Category",
          icon: PieChartIcon,
          color: "violet",
          description: "Breakdown of expenses by category",
          isCategory: true,
        };
      case "fixed-variable":
        return {
          title: "Fixed vs Variable Costs",
          icon: PieChartIcon,
          color: "indigo",
          description: "Breakdown of fixed recurring costs versus variable operational costs",
          isFixedVariable: true,
          value: (analytics?.fixed_expenses || 0) + (analytics?.variable_expenses || 0),
          insights: [
            { label: "Fixed Costs", value: analytics?.fixed_expenses, isPercentage: false, color: "blue" },
            { label: "Variable Costs", value: analytics?.variable_expenses, isPercentage: false, color: "violet" },
            { label: "Fixed %", value: ((analytics?.fixed_expenses || 0) + (analytics?.variable_expenses || 0)) > 0 ? `${((analytics?.fixed_expenses / ((analytics?.fixed_expenses || 0) + (analytics?.variable_expenses || 0))) * 100).toFixed(1)}%` : "0%", isPercentage: true, color: "blue" },
            { label: "Variable %", value: ((analytics?.fixed_expenses || 0) + (analytics?.variable_expenses || 0)) > 0 ? `${((analytics?.variable_expenses / ((analytics?.fixed_expenses || 0) + (analytics?.variable_expenses || 0))) * 100).toFixed(1)}%` : "0%", isPercentage: true, color: "violet" },
            // Expenses with no counterparty name cannot be split; shown so a reader
            // can see why fixed + variable does not add up to total spend: the
            // unattributed rows, and every expense kind the burn rate excludes.
            ...(((analytics?.fixed_variable_unattributed || 0) > 0)
              ? [{ label: "Unattributed (not split)", value: analytics?.fixed_variable_unattributed, color: "slate" }]
              : []),
          ]
        };
      case "monthly-trend":
        return {
          title: "Monthly Trend",
          icon: LineChartIcon,
          color: "blue",
          description: "Income and expense trends over time",
          isTrend: true,
        };
      default:
        return null;
    }
  };

  const config = getMetricConfig();

  if (!config) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Metric not found</p>
        <Button onClick={() => navigate(withPeriod("/analytics", selectedQuarter))} className="mt-4">
          Back to Analytics
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Monthly trend data
  const trendData = analytics?.monthly_trend?.map(m => {
    // Month format is now "January 2026", convert to "Jan 26"
    const parts = m.month.split(" ");
    const monthShort = parts[0]?.substring(0, 3) + " " + (parts[1]?.substring(2) || "");
    return {
      month: m.month,
      monthShort: monthShort,
      income: m.income,
      expense: m.expense,
      net: m.income - m.expense
    };
  }) || [];

  // Category data. The Total row printed under the table is the sum of these
  // rows and is labelled 100%, so each row's share has to use that same base
  // instead of analytics.total_expense, which also covers spend outside them.
  const categoryRows = analytics?.spend_by_category || [];
  const totalCategorySpend = categoryRows.reduce((sum, cat) => sum + (cat.amount || 0), 0);
  const categoryData = categoryRows.map((cat, idx) => ({
    name: cat.category,
    shortName: cat.category.length > 15 ? cat.category.substring(0, 15) + '...' : cat.category,
    value: cat.amount,
    // The backend emits a count per row; default it so neither the column nor
    // the footer total can render NaN or an empty cell.
    count: cat.count ?? 0,
    percentage: totalCategorySpend > 0 ? ((cat.amount / totalCategorySpend) * 100).toFixed(1) : "0.0",
    color: COLORS[idx % COLORS.length]
  }));

  // Fixed vs variable on the one base shared with AdminDashboardPage and
  // ExpensesPage: fixed + variable, guarded against a zero base.
  const fixedExpenses = analytics?.fixed_expenses || 0;
  // The per-category table shows EVERY expense kind, while the headline
  // fixed/variable figures cover operating spend only. Taking a percentage of
  // the headline rendered an intercompany category at 5000% of Fixed, so each
  // column is a share of its own column.
  // Summed over the rows each table actually DISPLAYS. Summing over every
  // category put 83% of the pool in rows the table filters out, so the
  // percentages did not add up and the reader could not see where the rest
  // had gone. `is_fixed` is "more fixed than variable", which is not the same
  // as "contributes to the fixed pool".
  const categoryFixedTotal = (analytics?.spend_by_category || [])
    .filter((cat) => cat.is_fixed)
    .reduce((sum, cat) => sum + (cat.fixed_amount || 0), 0);
  const categoryVariableTotal = (analytics?.spend_by_category || [])
    .filter((cat) => !cat.is_fixed)
    .reduce((sum, cat) => sum + (cat.variable_amount || 0), 0);
  const variableExpenses = analytics?.variable_expenses || 0;
  const fixedVariableBase = fixedExpenses + variableExpenses;
  // Expenses without a counterparty name cannot be split into fixed or variable.
  const fixedVariableUnattributed = analytics?.fixed_variable_unattributed || 0;

  // Runway projection data. The horizon comes from the backend's runway_months,
  // derived the same way as on AdminDashboardPage.
  const runwayData = [];
  if (analytics?.cash_balance && analytics?.burn_rate > 0) {
    let balance = analytics.cash_balance;
    const horizon = projectionHorizonMonths(analytics?.runway_months);
    for (let i = 0; i <= horizon; i++) {
      runwayData.push({
        month: `M${i}`,
        balance: Math.max(0, balance),
        burnRate: analytics.burn_rate
      });
      balance -= analytics.burn_rate;
    }
  }

  const IconComponent = config.icon;

  return (
    <div className="space-y-6" data-testid="metric-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(withPeriod("/analytics", selectedQuarter))}
            className="h-9 w-9 p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-${config.color}-100`}>
                <IconComponent className={`w-6 h-6 text-${config.color}-600`} />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">{config.title}</h1>
            </div>
            <p className="text-slate-500 mt-1 ml-12">{config.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <Select value={selectedQuarter} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main Value Card */}
      {!config.isCategory && !config.isTrend && (
        <Card className={`border-0 shadow-lg bg-gradient-to-br from-${config.color}-50 to-white`}>
          <CardContent className="p-8">
            <div className="text-center">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Current Value</p>
              <p className={`text-5xl font-bold mt-2 text-${config.color}-600`}>
                {config.isInfinity ? "∞" : formatCurrency(config.value)}
                {config.suffix && !config.isInfinity && <span className="text-2xl font-normal">{config.suffix}</span>}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insights Grid */}
      {config.insights && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {config.insights.map((insight, idx) => (
            <Card key={idx} className="border-0 shadow-sm">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{insight.label}</p>
                <p className={`text-2xl font-bold mt-1 text-${insight.color}-600`}>
                  {insight.isPercentage || insight.isStatus ? insight.value : formatCurrency(insight.value)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Fixed vs Variable Explanation Card */}
      {config.isFixedVariable && (
        <Card className="border-0 shadow-sm bg-slate-50">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-slate-900">How costs are classified</h4>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="text-blue-600 font-medium">Fixed costs</span> are from vendors that appear in <strong>3 or more different months</strong> (recurring payments like subscriptions, rent, salaries).
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="text-violet-600 font-medium">Variable costs</span> are from vendors with <strong>less than 3 months</strong> of transactions (one-time purchases, irregular expenses).
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  This classification is based on vendor payment frequency, not category labels.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">Data Visualization</CardTitle>
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList>
                <TabsTrigger value="chart">Chart</TabsTrigger>
                <TabsTrigger value="table">Table</TabsTrigger>
                {(metricType === "runway" || metricType === "burn-rate") && (
                  <TabsTrigger value="projection">Projection</TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {/* Category Spend Views */}
          {config.isCategory && (
            <>
              {viewMode === "chart" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ shortName, percentage }) => `${shortName} (${percentage}%)`}
                          labelLine={false}
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          formatter={(value) => formatCurrency(value)}
                          labelFormatter={(label) => categoryData.find(c => c.shortName === label)?.name || label}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Bar Chart */}
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={categoryData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis type="number" tickFormatter={(v) => `€${(v/1000).toFixed(0)}K`} />
                        <YAxis type="category" dataKey="shortName" width={100} tick={{ fontSize: 12 }} />
                        <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              {viewMode === "table" && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">% of Total</TableHead>
                      <TableHead className="text-right">Transactions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categoryData.map((cat, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                            {cat.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(cat.value)}</TableCell>
                        <TableCell className="text-right">{cat.percentage}%</TableCell>
                        <TableCell className="text-right">{cat.count}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-slate-50 font-bold">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalCategorySpend)}</TableCell>
                      <TableCell className="text-right">100%</TableCell>
                      <TableCell className="text-right">{categoryData.reduce((s, c) => s + (c.count ?? 0), 0)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </>
          )}

          {/* Monthly Trend Views */}
          {config.isTrend && (
            <>
              {viewMode === "chart" && (
                <div className="space-y-6">
                  {/* Area Chart */}
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="monthShort" 
                          angle={-45} 
                          textAnchor="end" 
                          height={60} 
                          fontSize={11}
                          interval={0}
                        />
                        <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}K`} />
                        <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Area type="monotone" dataKey="income" name="Income" fill="#10b98140" stroke="#10b981" />
                        <Area type="monotone" dataKey="expense" name="Expense" fill="#ef444440" stroke="#ef4444" />
                        <Line type="monotone" dataKey="net" name="Net" stroke="#3b82f6" strokeWidth={2} dot />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              {viewMode === "table" && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Income</TableHead>
                      <TableHead className="text-right">Expenses</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trendData.map((month, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{month.month}</TableCell>
                        <TableCell className="text-right text-emerald-600">{formatCurrency(month.income)}</TableCell>
                        <TableCell className="text-right text-red-500">{formatCurrency(month.expense)}</TableCell>
                        <TableCell className={`text-right font-semibold ${month.net >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {formatCurrency(month.net)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}

          {/* Runway/Burn Rate Projection */}
          {(metricType === "runway" || metricType === "burn-rate") && viewMode === "projection" && (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={runwayData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45} 
                    textAnchor="end" 
                    height={60} 
                    fontSize={11}
                    interval={0}
                  />
                  <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}K`} />
                  <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  <Area 
                    type="monotone" 
                    dataKey="balance" 
                    name="Projected Balance"
                    fill="#3b82f640" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
              <p className="text-sm text-slate-500 text-center mt-4">
                <Info className="w-4 h-4 inline mr-1" />
                Projection based on current burn rate of {formatCurrency(analytics?.burn_rate)}/month
              </p>
            </div>
          )}

          {/* Fixed vs Variable Costs Chart */}
          {config.isFixedVariable && viewMode === "chart" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Fixed Costs', value: analytics?.fixed_expenses || 0, fill: '#3b82f6' },
                        { name: 'Variable Costs', value: analytics?.variable_expenses || 0, fill: '#8b5cf6' }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                    </Pie>
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Monthly Fixed vs Variable Trend */}
              <div className="h-80">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Monthly Breakdown</h4>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={analytics?.spend_by_category_monthly?.slice(-12) || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      angle={-45} 
                      textAnchor="end" 
                      height={60} 
                      fontSize={10}
                      interval={0}
                      tickFormatter={(v) => {
                        const parts = v.split(" ");
                        return parts[0]?.substring(0, 3) + " " + (parts[1]?.substring(2) || "");
                      }}
                    />
                    <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}K`} />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="fixed" name="Fixed" stackId="a" fill="#3b82f6" />
                    <Bar dataKey="variable" name="Variable" stackId="a" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {config.isFixedVariable && viewMode === "table" && (
            <div className="space-y-6">
              {/* Summary Table */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Cost Summary</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cost Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Percentage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded bg-blue-500"></div>
                          Fixed Costs
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(analytics?.fixed_expenses)}</TableCell>
                      <TableCell className="text-right">
                        {fixedVariableBase > 0 ? ((fixedExpenses / fixedVariableBase) * 100).toFixed(1) : 0}%
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded bg-violet-500"></div>
                          Variable Costs
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(analytics?.variable_expenses)}</TableCell>
                      <TableCell className="text-right">
                        {fixedVariableBase > 0 ? ((variableExpenses / fixedVariableBase) * 100).toFixed(1) : 0}%
                      </TableCell>
                    </TableRow>
                    {fixedVariableUnattributed > 0 && (
                      <TableRow>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-slate-300"></div>
                            Unattributed (no counterparty, cannot be split)
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(fixedVariableUnattributed)}</TableCell>
                        <TableCell className="text-right text-slate-400">n/a</TableCell>
                      </TableRow>
                    )}
                    <TableRow className="bg-slate-50 font-semibold">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-right">{formatCurrency(fixedVariableBase)}</TableCell>
                      <TableCell className="text-right">100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <p className="text-xs text-slate-400 mt-2">
                  Fixed plus variable plus the unattributed operating spend of{" "}
                  {formatCurrency(analytics?.fixed_variable_unattributed_operating)} comes
                  to {formatCurrency(analytics?.fixed_variable_basis)}, which is the
                  operating spend the burn rate is built from. Total spend of{" "}
                  {formatCurrency(analytics?.total_expense)} also includes group funding and
                  financing costs, which the burn rate leaves out.
                  {fixedVariableUnattributed > 0
                    ? ` The split is made per counterparty, so ${formatCurrency(fixedVariableUnattributed)} of spend with no counterparty name stays unattributed across all kinds.`
                    : ""}
                </p>
              </div>

              {/* Fixed Costs Detail */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Fixed Costs by Category</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">% of Fixed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics?.spend_by_category
                      ?.filter(cat => cat.is_fixed)
                      ?.map((cat, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{cat.category}</TableCell>
                          {/* The fixed slice, so the amount and the share
                              beside it describe the same quantity. */}
                          <TableCell className="text-right">{formatCurrency(cat.fixed_amount)}</TableCell>
                          <TableCell className="text-right">
                            {categoryFixedTotal > 0 ? (((cat.fixed_amount || 0) / categoryFixedTotal) * 100).toFixed(1) : 0}%
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>

              {/* Variable Costs Detail */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Variable Costs by Category</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">% of Variable</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics?.spend_by_category
                      ?.filter(cat => !cat.is_fixed)
                      ?.map((cat, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{cat.category}</TableCell>
                          <TableCell className="text-right">{formatCurrency(cat.variable_amount)}</TableCell>
                          <TableCell className="text-right">
                            {categoryVariableTotal > 0 ? (((cat.variable_amount || 0) / categoryVariableTotal) * 100).toFixed(1) : 0}%
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Generic Chart for other metrics */}
          {!config.isCategory && !config.isTrend && !config.isFixedVariable && viewMode === "chart" && (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="monthShort" 
                    angle={-45} 
                    textAnchor="end" 
                    height={60} 
                    fontSize={11}
                    interval={0}
                  />
                  <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}K`} />
                  <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Area type="monotone" dataKey="income" name="Income" fill="#10b98140" stroke="#10b981" />
                  <Area type="monotone" dataKey="expense" name="Expenses" fill="#ef444440" stroke="#ef4444" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {!config.isCategory && !config.isTrend && !config.isFixedVariable && viewMode === "table" && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Income</TableHead>
                  <TableHead className="text-right">Expenses</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trendData.map((month, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{month.month}</TableCell>
                    <TableCell className="text-right text-emerald-600">{formatCurrency(month.income)}</TableCell>
                    <TableCell className="text-right text-red-500">{formatCurrency(month.expense)}</TableCell>
                    <TableCell className={`text-right font-semibold ${month.net >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {formatCurrency(month.net)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MetricDetailPage;
